import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api";
import { fmtDate } from "../fmt";

/**
 * Review inbox — the human gate over AI categorize suggestions the engine was
 * not confident enough to auto-apply. Accepting applies the pick to the
 * transaction and teaches a deterministic rule; rejecting closes it. "Auto-
 * categorize now" runs the AI pass over any still-uncategorized transactions.
 */

interface Suggestion {
  id: string;
  field: string;
  confidence: number;
  rationale: string | null;
  createdAt: string;
  narration: string | null;
  partyName: string | null;
  categoryName: string | null;
}

interface Status {
  configured: boolean;
  enabled: boolean;
}

export function Review() {
  const [items, setItems] = useState<Suggestion[]>([]);
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([
        api.get<{ suggestions: Suggestion[] }>("/api/ai/suggestions"),
        api.get<Status>("/api/ai/status"),
      ]);
      setItems(s.suggestions);
      setStatus(st);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function resolve(id: string, action: "accept" | "reject") {
    setItems((xs) => xs.filter((x) => x.id !== id)); // optimistic
    try {
      await api.post(`/api/ai/suggestions/${id}/resolve`, { action });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
      void load();
    }
  }

  async function categorizeNow() {
    setRunning(true);
    setNote(null);
    setError(null);
    try {
      const res = await api.post<{ summary: { autoApplied: number; queued: number; aiSkipped: boolean } }>(
        "/api/ai/categorize",
        {}
      );
      const s = res.summary;
      setNote(
        s.aiSkipped
          ? "AI is off or over budget — nothing was categorized."
          : `Auto-applied ${s.autoApplied}, queued ${s.queued} for review.`
      );
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? "AI is not enabled for this workspace."
          : err instanceof Error
            ? err.message
            : "Failed"
      );
    } finally {
      setRunning(false);
    }
  }

  const disabled = status ? !status.configured || !status.enabled : false;

  return (
    <div className="screen">
      <div className="screen-head">
        <h1>Review inbox</h1>
        <p className="muted">AI-suggested categorizations awaiting your confirmation.</p>
      </div>

      {disabled && (
        <div className="banner warn">
          AI isn't enabled. <Link to="/settings">Enable it in Settings →</Link>
        </div>
      )}

      <div className="toolbar">
        <button type="button" onClick={() => void categorizeNow()} disabled={disabled || running}>
          {running ? "Categorizing…" : "Auto-categorize now"}
        </button>
        <button type="button" className="ghost" onClick={() => void load()} disabled={loading}>
          Refresh
        </button>
        {note && <span className="muted">{note}</span>}
      </div>

      {error && <p className="error">{error}</p>}
      {loading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted">Nothing to review — the queue is clear. 🎉</p>
      ) : (
        <ul className="review-list">
          {items.map((s) => (
            <li key={s.id} className="review-card">
              <div className="review-main">
                <div className="review-narration">{s.narration ?? "(no narration)"}</div>
                <div className="review-picks">
                  {s.partyName && <span className="tag">Party: {s.partyName}</span>}
                  {s.categoryName && <span className="tag">Category: {s.categoryName}</span>}
                  <span className="tag conf">{Math.round(s.confidence * 100)}% confident</span>
                </div>
                {s.rationale && <div className="review-why muted">{s.rationale}</div>}
                <div className="review-meta muted">{fmtDate(s.createdAt)}</div>
              </div>
              <div className="review-actions">
                <button type="button" onClick={() => void resolve(s.id, "accept")}>
                  Accept
                </button>
                <button type="button" className="ghost" onClick={() => void resolve(s.id, "reject")}>
                  Reject
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
